import { buttonVariants } from "@/components/atoms/buttons";
import { CheckoutRouteGuard } from "@/components/layouts/checkout-route-guard";
import StageHeader from "@/components/organisms/stage-header";
import {
  isCustomerInfoRoute,
  isDeliveryRoute,
  isEventInfoRoute,
  isRecapRoute,
  isThankYouRoute,
} from "@/components/templates/Checkout";
import PaymentSidebar from "@/components/templates/Checkout/components/checkout-sidebar";
import CompleteCart from "@/components/templates/Checkout/components/complete-cart-button";
import CheckoutAttemptTracker from "@/components/templates/Checkout/components/checkout-attempt-tracker";
import { CheckoutAttemptStage } from "@/lib/checkout-attempt";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { cn, getCheckoutBackRoute } from "@/lib/utils";
import { ChevronLeft } from "lucide-react";
import Link from "next/link";

type CheckoutLayoutProps = {
  locale: Locales;
  children: React.ReactNode;
  showBackButton?: boolean;
  route: string;
  className?: string;
};

const CheckoutLayout = ({ locale, children, showBackButton, route, className }: CheckoutLayoutProps) => {
  const resolveTitle = () => {
    if (isEventInfoRoute(locale, route)) return __("Quote - Step 1", locale);
    if (isCustomerInfoRoute(locale, route)) return __("Quote - Step 2", locale);
    if (isDeliveryRoute(locale, route)) return __("Quote - Step 3", locale);
    if (isRecapRoute(locale, route)) return __("Summary of your quote", locale);
  };

  const trackingStage: CheckoutAttemptStage = isEventInfoRoute(locale, route)
    ? "event_information"
    : isCustomerInfoRoute(locale, route)
      ? "customer_information"
      : isDeliveryRoute(locale, route)
        ? "delivery"
        : "recap";

  return (
    <CheckoutRouteGuard locale={locale} route={route}>
      {!isThankYouRoute(locale, route) && <CheckoutAttemptTracker stage={trackingStage} />}
      {!isThankYouRoute(locale, route) && (
        <StageHeader
          locale={locale}
          className="md:my-4"
          orientationOnMobile="left"
          title={resolveTitle()}
          description={[
            {
              type: "paragraph",
              text: `${__("Trust Festi Rent for your parties and events.", locale)}`,
              spans: [],
            },
            {
              type: "paragraph",
              text: `${__("We are happy to advise you on choosing the equipment that will suit you perfectly.", locale)}`,
              spans: [],
            },
          ]}
        />
      )}
      {!isThankYouRoute(locale, route) && (
        <div className="text-dark-primary bg-[#FBF9F899]">
          <div className="container space-y-7.5 px-3.5 pt-7.5 pb-9.5 md:p-7.5">
            <div className={cn("flex flex-col gap-12 md:gap-12.5 lg:flex-row", className)}>
              {children}
              <PaymentSidebar
                route={route}
                locale={locale}
                showPromoInput={isEventInfoRoute(locale, route) || isCustomerInfoRoute(locale, route)}
                variant={isEventInfoRoute(locale, route) ? "default" : "secondary"}
              />
              {isRecapRoute(locale, route) && (
                <div className="fixed right-0 bottom-0 left-0 z-40 flex w-full items-center gap-4 bg-white p-4 sm:hidden">
                  <Link
                    href={getCheckoutBackRoute(locale, route)}
                    className={cn(buttonVariants({ variant: "secondary", size: "md" }), "border-primary-light")}
                  >
                    {__("Previous", locale)}
                  </Link>
                  <CompleteCart locale={locale} className="flex-1" />
                </div>
              )}
            </div>
            {showBackButton && (
              <Link
                href={getCheckoutBackRoute(locale, route)}
                className="text-primary-light flex w-fit items-center gap-2.5 pr-1 text-start font-medium transition-transform duration-150 ease-in-out active:scale-95 max-md:hidden"
              >
                <ChevronLeft size={24} aria-hidden="true" />
                {__("Return to the previous step", locale)}
              </Link>
            )}
          </div>
        </div>
      )}
      {isThankYouRoute(locale, route) && children}
    </CheckoutRouteGuard>
  );
};

export default CheckoutLayout;
